import * as React from 'react';

import { useNavigate } from 'react-router-dom';

import {
    ColumnDatatable,
    FilterDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { DataSource, getDataThunkType } from '@europrocurement/l2d-redux-utils';
import {
    ExceptionFacturante,
    ExceptionFacturanteApiObject,
} from '@europrocurement/l2d-domain/reducers/achats/slices/exceptionFacturante';
import { useDispatch, useSelector } from 'react-redux';
import {
    DEF_FREQUENCE_RELANCE_ACHATS_DATASOURCE_NAME,
    DefFrequenceApiObject,
} from '@europrocurement/l2d-domain/reducers/achats/slices/defFrequenceSlice';
import { AchatsAppDispatch, AchatsStateType, useAchatsService } from '@europrocurement/l2d-domain';
import { usePatchExceptionFacturante } from '../../../../hooks/exceptionFacturanteHooks';
import DeleteExceptionFacturanteButton from './DeleteExceptionFacturanteButton';
import AsyncSwitchExceptionFacturanteRelanceAuto from '../fragments/AsyncSwitchExceptionFacturanteRelanceAuto';
import { FrequencySelector } from '../fragments/FrequencySelector';

export type ExceptionsFacturantesListComponentProps = {
    dataSource: DataSource<ExceptionFacturante>;
    fetchData: getDataThunkType<ExceptionFacturanteApiObject>;
    filtersControl?: boolean;
    filterFilters?: (
        filtre: FilterDatatable,
        index: number,
        array: FiltersDatatableList,
    ) => unknown;
    disabledToggles?: Partial<Record<'manuel' | 'bloquant' | 'relanceAuto', boolean>>;
};

const ExceptionsFacturantesListComponent: React.FunctionComponent<ExceptionsFacturantesListComponentProps> =
    function ({
        dataSource,
        fetchData,
        filtersControl = true,
        filterFilters = () => true,
        disabledToggles,
    }) {
        const navigate = useNavigate();
        const dispatch = useDispatch<AchatsAppDispatch>();
        const patchExceptionFacturante = usePatchExceptionFacturante();

        const { defFrequenceDataSourcesThunks } = useAchatsService();

        const frequencyRelanceAchatsDatasource = useSelector<AchatsStateType>(
            (s) => s.achats.frequence[DEF_FREQUENCE_RELANCE_ACHATS_DATASOURCE_NAME],
        ) as DataSource<DefFrequenceApiObject>;

        const frequencyRelanceAchatsFetchData =
            defFrequenceDataSourcesThunks[DEF_FREQUENCE_RELANCE_ACHATS_DATASOURCE_NAME].data
                .fetchRelanceAchatsFrequences;

        if (!!filtersControl === true) {
            console.debug(filtersControl, filterFilters);
        }

        const columns: ColumnDatatable<ExceptionFacturante>[] = React.useMemo(
            () => [
                {
                    label: 'Id',
                    isDisplayed: true,
                    render: ({ id }) => id,
                    field: 'id',
                },
                {
                    label: 'Code Rejet',
                    isDisplayed: true,
                    render: ({ codeRejet }) => codeRejet?.split('/')[3],
                    field: 'codeRejet',
                },
                {
                    label: 'Relance active',
                    isDisplayed: true,
                    onClickCell: () => {},
                    render: (exceptionFacturante) =>
                        exceptionFacturante.id ? (
                            <AsyncSwitchExceptionFacturanteRelanceAuto
                                fetchData={fetchData}
                                exceptionFacturante={exceptionFacturante}
                                disabled={disabledToggles?.relanceAuto}
                            />
                        ) : null,
                },
                {
                    label: 'Fréquence de relance',
                    isDisplayed: true,
                    onClickCell: () => {},
                    render: (exceptionFacturante) => (
                        <FrequencySelector
                            variant="exceptionFacturante"
                            exceptionFacturante={exceptionFacturante}
                            fetchExceptionFacturanteData={fetchData}
                            patchExceptionFacturante={patchExceptionFacturante}
                            frequencyDatasource={frequencyRelanceAchatsDatasource}
                            fetchFrequencies={() => dispatch(frequencyRelanceAchatsFetchData({}))}
                        />
                    ),
                },
                {
                    label: 'Actions',
                    isDisplayed: true,
                    onClickCell: () => {},
                    render: ({ id }) =>
                        id ? <DeleteExceptionFacturanteButton exceptionFacturanteId={id} /> : null,
                },
            ],
            [
                disabledToggles?.relanceAuto,
                dispatch,
                fetchData,
                frequencyRelanceAchatsDatasource,
                frequencyRelanceAchatsFetchData,
                patchExceptionFacturante,
            ],
        );

        const filters: FiltersDatatableList = [];

        return (
            <StoreDatatable
                dataSource={dataSource}
                columns={columns}
                fetchData={fetchData}
                filters={filters}
                localStorageKey="FlexyStoreDatatableRejectCodes"
                localStorageRefreshDate={new Date('2023-06-23T09:42:00.000Z')}
                fetchWithoutFilter
                onClickRow={(e: React.MouseEvent<HTMLElement>, item: ExceptionFacturante) =>
                    navigate(`${item.code}`, {
                        replace: true,
                    })
                }
                onWheelClickRow={(e: React.MouseEvent<HTMLElement>, item: ExceptionFacturante) =>
                    navigate(`${item.code}`, {
                        replace: true,
                    })
                }
            />
        );
    };

export default ExceptionsFacturantesListComponent;
